import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Stack,
    Chip,
    Divider,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// ---------------------------------------------------------------------------
// CompanyMessages
// Settings > Agency tab section for posting messages to employees.
//
// Props:
//   comp_id  — company ID
//   user     — current user object (emp_id used as created_by)
// ---------------------------------------------------------------------------
const CompanyMessages = (props) => {
    const [msgText, setMsgText] = useState('');
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [validUntil, setValidUntil] = useState(null);
    const [existingMessages, setExistingMessages] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', text }

    // ------------------------------------------------------------------
    // Load roles from API and existing active messages
    // ------------------------------------------------------------------
    useEffect(() => {
        loadRoles();
        loadMessages();
    }, []);

    const loadRoles = async () => {
        try {
            const response = await axios.get(APIURL + `/dropdowns/dropdownType/empType?comp_id=${props.comp_id}`);
            const fetched = (response.data || []).map(r => ({ label: r, value: r }));
            setRoles(fetched);
            setSelectedRoles([]); // empty = all roles
        } catch (err) {
            console.error('CompanyMessages: failed to load roles', err);
        }
    };

    const loadMessages = async () => {
        setLoadingList(true);
        try {
            const response = await axios.get(APIURL + '/messages', {
                params: { comp_id: props.comp_id },
            });
            // Show only company-sourced messages for this comp_id in the admin list
            setExistingMessages(
                (response.data || []).filter(m => m.msg_source === 'company' && m.comp_id === props.comp_id)
            );
        } catch (err) {
            console.error('CompanyMessages: failed to load messages', err);
        } finally {
            setLoadingList(false);
        }
    };

    // ------------------------------------------------------------------
    // Toggle role checkbox
    // ------------------------------------------------------------------
    const handleRoleToggle = (roleValue) => {
        setSelectedRoles(prev =>
            prev.includes(roleValue)
                ? prev.filter(r => r !== roleValue)
                : [...prev, roleValue]
        );
    };

    // ------------------------------------------------------------------
    // Send message
    // ------------------------------------------------------------------
    const handleSend = async () => {
        if (!msgText.trim()) return;
        setSaving(true);
        setFeedback(null);
        try {
            await axios.post(APIURL + '/messages', {
                msg_text: msgText.trim(),
                msg_source: 'company',
                target_roles: selectedRoles.length > 0 ? selectedRoles : null,
                valid_until: validUntil ? validUntil.toISOString() : null,
                comp_id: props.comp_id,
                created_by: props.user.emp_id,
            });
            setFeedback({ type: 'success', text: 'Message sent successfully.' });
            setMsgText('');
            setSelectedRoles([]);
            setValidUntil(null);
            await loadMessages();
        } catch (err) {
            console.error('CompanyMessages: failed to send message', err);
            setFeedback({ type: 'error', text: 'Failed to send message. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    // ------------------------------------------------------------------
    // Deactivate an existing message
    // ------------------------------------------------------------------
    const handleDeactivate = async (msg_id) => {
        try {
            await axios.put(`${APIURL}/messages/${msg_id}/deactivate`);
            setExistingMessages(prev => prev.filter(m => m.msg_id !== msg_id));
        } catch (err) {
            console.error('CompanyMessages: failed to deactivate message', err);
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
                {/* Left column — section description */}
                <Grid size={4}>
                    <Stack spacing={1}>
                        <Typography variant="h5">Company Messages</Typography>
                        <Typography variant="body1" sx={{ color: 'gray' }}>
                            Send a message to employees. Messages appear on their home screen
                            until dismissed
                        </Typography>
                    </Stack>
                </Grid>

                {/* Right column — form */}
                <Grid size={8}>
                    <Stack spacing={2}>
                        {/* Message text */}
                        <Box>
                            <TextField
                                label="Message"
                                multiline
                                minRows={3}
                                maxRows={4}
                                fullWidth
                                value={msgText}
                                onChange={e => setMsgText(e.target.value.slice(0, 255))}
                                inputProps={{ maxLength: 255 }}
                                placeholder="Type your message here…"
                            />
                            <Typography
                                variant="caption"
                                color={msgText.length >= 240 ? 'error' : 'text.secondary'}
                                sx={{ display: 'block', textAlign: 'right', mt: 0.25 }}
                            >
                                {msgText.length}/255
                            </Typography>
                        </Box>

                        {/* Role targeting */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Visible to roles
                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                    (leave all unchecked to send to everyone)
                                </Typography>
                            </Typography>
                            <FormGroup row>
                                {roles.map(role => (
                                    <FormControlLabel
                                        key={role.value}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={selectedRoles.includes(role.value)}
                                                onChange={() => handleRoleToggle(role.value)}
                                            />
                                        }
                                        label={role.label}
                                    />
                                ))}
                            </FormGroup>
                        </Box>

                        {/* Valid until */}
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Valid Until (optional)"
                                    value={validUntil}
                                    onChange={(newValue) => setValidUntil(newValue)}
                                    minDate={new Date()}
                                    slotProps={{ textField: { size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Box>

                        {/* Feedback */}
                        {feedback && (
                            <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
                                {feedback.text}
                            </Alert>
                        )}

                        {/* Send button */}
                        <Box>
                            <Button
                                variant="contained"
                                onClick={handleSend}
                                disabled={saving || !msgText.trim()}
                                startIcon={saving ? <CircularProgress size={16} /> : null}
                            >
                                {saving ? 'Sending…' : 'Send Message'}
                            </Button>
                        </Box>

                        <Divider />

                        {/* Existing active messages */}
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Active Messages
                            </Typography>
                            {loadingList ? (
                                <CircularProgress size={24} />
                            ) : existingMessages.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No active messages.
                                </Typography>
                            ) : (
                                <Paper variant="outlined" sx={{
                                    maxHeight: 300,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-track': { background: '#fafafa' },
                                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#bebebe', borderRadius: '22px' },
                                }}>
                                    <Table
                                        size="small"
                                        stickyHeader
                                        sx={{
                                            tableLayout: 'fixed',
                                            width: '100%',
                                            '& th, & td': { px: 1.25 },
                                        }}
                                    >
                                        <colgroup>
                                            <col style={{ width: '40%' }} />
                                            <col style={{ width: '18%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '14%' }} />
                                        </colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Message</TableCell>
                                                <TableCell>Roles</TableCell>
                                                <TableCell>Valid Until</TableCell>
                                                <TableCell>Created</TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {existingMessages.map(msg => {
                                                let roleChips = null;
                                                if (msg.target_roles) {
                                                    try {
                                                        const roles = JSON.parse(msg.target_roles);
                                                        roleChips = roles.map(r => (
                                                            <Chip
                                                                key={r}
                                                                label={r}
                                                                size="small"
                                                                sx={{
                                                                    mr: 0.5,
                                                                    maxWidth: '100%',
                                                                    '& .MuiChip-label': {
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    },
                                                                }}
                                                            />
                                                        ));
                                                    } catch {
                                                        roleChips = msg.target_roles;
                                                    }
                                                }
                                                return (
                                                    <TableRow key={msg.msg_id}>
                                                        <TableCell sx={{ wordBreak: 'break-word' }}>
                                                            {msg.msg_text}
                                                        </TableCell>
                                                        <TableCell sx={{ overflowWrap: 'anywhere' }}>
                                                            {roleChips || <Typography variant="caption" color="text.secondary">All</Typography>}
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'normal' }}>
                                                            {msg.valid_until
                                                                ? moment(msg.valid_until).format('MMM D, YYYY')
                                                                : <Typography variant="caption" color="text.secondary">Never</Typography>}
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'normal' }}>
                                                            {moment(msg.created_at).format('MMM D, YYYY')}
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: 'center' }}>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                variant="outlined"
                                                                onClick={() => handleDeactivate(msg.msg_id)}
                                                                sx={{
                                                                    fontSize: '0.72rem',
                                                                    minWidth: 0,
                                                                    maxWidth: '100%',
                                                                    px: 1,
                                                                }}
                                                            >
                                                                Deactivate
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            )}
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CompanyMessages;
